/**
 * CL-396A / CL-400F — Guided CMA builder step definitions and helpers.
 */

import { buildPropertyProfileSummary } from "./clientPropertyMarketContext";
import { resolvePropertyCardAddress } from "./clientMyProperties";

function safeStr(value) {
  return String(value ?? "").trim();
}

export const CMA_BUILDER_STEPS = [
  {
    id: "subject",
    label: "Subject property",
    shortLabel: "Subject",
    description: "Confirm the property details that anchor this estimate.",
  },
  {
    id: "baseline_market",
    label: "Baseline & market context",
    shortLabel: "Baseline & context",
    description:
      "Municipal assessment baseline, optional manual override, and assessment-roll background (not MLS comps).",
  },
  {
    id: "renovations",
    label: "Renovations & upgrades",
    shortLabel: "Renovations",
    description: "Itemized renovation adjustments — the only renovation dollar lines in this CMA.",
  },
  {
    id: "version2",
    label: "MLS comps",
    shortLabel: "MLS comps",
    description: "Manual MLS sold/active comparables, line-item adjustments vs subject, and Version 2 metrics.",
  },
  {
    id: "summary",
    label: "CMA summary",
    shortLabel: "Summary",
    description:
      "Final summary — Version 1 assessment estimate, adjusted MLS comps (Version 2), and professional disclaimer.",
  },
];

const STEP_IDS = new Set(CMA_BUILDER_STEPS.map((s) => s.id));

const LEGACY_CMA_BUILDER_STEP_ALIASES = {
  baseline: "baseline_market",
  context: "baseline_market",
  market: "baseline_market",
  comps: "version2",
  mls_comps: "version2",
};

export function normalizeCmaBuilderStep(value) {
  const key = safeStr(value).toLowerCase();
  if (LEGACY_CMA_BUILDER_STEP_ALIASES[key]) return LEGACY_CMA_BUILDER_STEP_ALIASES[key];
  return STEP_IDS.has(key) ? key : CMA_BUILDER_STEPS[0].id;
}

export function findCmaBuilderStep(stepId) {
  const id = normalizeCmaBuilderStep(stepId);
  return CMA_BUILDER_STEPS.find((s) => s.id === id) || CMA_BUILDER_STEPS[0];
}

export function getCmaBuilderStepIndex(stepId) {
  const id = normalizeCmaBuilderStep(stepId);
  return CMA_BUILDER_STEPS.findIndex((s) => s.id === id);
}

export function getNextCmaBuilderStep(stepId) {
  const idx = getCmaBuilderStepIndex(stepId);
  if (idx < 0 || idx >= CMA_BUILDER_STEPS.length - 1) return null;
  return CMA_BUILDER_STEPS[idx + 1];
}

export function getPrevCmaBuilderStep(stepId) {
  const idx = getCmaBuilderStepIndex(stepId);
  if (idx <= 0) return null;
  return CMA_BUILDER_STEPS[idx - 1];
}

/**
 * Subject property snapshot for CMA step 1 — property record + Calgary assessment when matched.
 * @param {object|null|undefined} profile
 */
export function buildCmaSubjectPropertySnapshot(profile) {
  const p = profile && typeof profile === "object" ? profile : {};
  const summary = buildPropertyProfileSummary(p);
  const snap = summary.parcelSnapshot;
  const address =
    safeStr(p.myPropertyFullAddress) ||
    safeStr(p.myPropertyAddressLine) ||
    safeStr(snap?.address) ||
    resolvePropertyCardAddress(p) ||
    null;

  const livingAreaSf =
    snap?.gross_floor_area != null
      ? Number(snap.gross_floor_area)
      : snap?.total_floor_area != null
        ? Number(snap.total_floor_area)
        : null;

  return {
    address,
    community: summary.community || safeStr(p.myPropertyNeighbourhood) || null,
    propertyType:
      safeStr(p.myPropertyType) ||
      summary.propertyType ||
      summary.rollTypeLabel ||
      null,
    yearBuilt: summary.yearBuilt,
    lotSizeLabel: summary.lotSizeLabel,
    assessedValue: summary.currentAssessedValue,
    hasParcel: summary.hasParcel,
    bedrooms: snap?.bedrooms != null ? String(snap.bedrooms) : null,
    bathrooms: snap?.bathrooms != null ? String(snap.bathrooms) : null,
    livingAreaLabel:
      livingAreaSf != null && Number.isFinite(livingAreaSf) && livingAreaSf > 0
        ? `${Math.round(livingAreaSf).toLocaleString()} sf`
        : null,
    rollNumber: safeStr(snap?.roll_number) || safeStr(p.myPropertyParcelRoll) || null,
  };
}
